import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  {
    code: 'nl',
    label: 'Nederlands',
    flag: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 9 6" className="w-6 h-auto rounded-sm">
        <rect width="9" height="2" fill="#AE1C28"/>
        <rect y="2" width="9" height="2" fill="#fff"/>
        <rect y="4" width="9" height="2" fill="#21468B"/>
      </svg>
    ),
  },
  {
    code: 'en',
    label: 'English',
    flag: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" className="w-6 h-auto rounded-sm">
        <clipPath id="en-t"><path d="M0 0v30h60V0z"/></clipPath>
        <clipPath id="en-s"><path d="M30 15h30v15zv15H0zH0V0zV0h30z"/></clipPath>
        <g clipPath="url(#en-t)">
          <path d="M0 0v30h60V0z" fill="#012169"/>
          <path d="M0 0l60 30m0-30L0 30" stroke="#fff" strokeWidth="6"/>
          <path d="M0 0l60 30m0-30L0 30" stroke="#C8102E" strokeWidth="4" clipPath="url(#en-s)"/>
          <path d="M30 0v30M0 15h60" stroke="#fff" strokeWidth="10"/>
          <path d="M30 0v30M0 15h60" stroke="#C8102E" strokeWidth="6"/>
        </g>
      </svg>
    ),
  },
  {
    code: 'fr',
    label: 'Français',
    flag: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 2" className="w-6 h-auto rounded-sm">
        <rect width="1" height="2" fill="#002395"/>
        <rect x="1" width="1" height="2" fill="#fff"/>
        <rect x="2" width="1" height="2" fill="#ED2939"/>
      </svg>
    ),
  },
  {
    code: 'de',
    label: 'Deutsch',
    flag: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 5 3" className="w-6 h-auto rounded-sm">
        <rect width="5" height="1" fill="#000"/>
        <rect y="1" width="5" height="1" fill="#D00"/>
        <rect y="2" width="5" height="1" fill="#FFCE00"/>
      </svg>
    ),
  },
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Normalise language code: 'nl-NL' → 'nl'
  const currentCode = i18n.language?.slice(0, 2);
  const current = LANGUAGES.find((l) => l.code === currentCode) ?? LANGUAGES[0];

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function select(code) {
    i18n.changeLanguage(code);
    setOpen(false);
  }

  // Keyboard navigation
  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Enter' || e.key === ' ') {
      setOpen((v) => !v);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      // focus first item
      containerRef.current?.querySelector('[role="menuitem"]')?.focus();
    }
  }

  function handleItemKeyDown(e, code, index) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      select(code);
    } else if (e.key === 'Escape') {
      setOpen(false);
      containerRef.current?.querySelector('[aria-haspopup="true"]')?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const items = containerRef.current?.querySelectorAll('[role="menuitem"]');
      items?.[index + 1]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const items = containerRef.current?.querySelectorAll('[role="menuitem"]');
      if (index === 0) {
        containerRef.current?.querySelector('[aria-haspopup="true"]')?.focus();
        setOpen(false);
      } else {
        items?.[index - 1]?.focus();
      }
    }
  }

  const options = LANGUAGES.filter((l) => l.code !== current.code);

  return (
    <div ref={containerRef} className="relative" style={{ zIndex: 50 }}>
      {/* Trigger button */}
      <button
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={current.label}
        title={current.label}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleKeyDown}
        className="flex items-center gap-1.5 px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        {current.flag}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-3 h-3 text-gray-400"
          aria-hidden="true"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
        >
          <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd"/>
        </svg>
      </button>

      {/* Dropdown menu */}
      {open && (
        <div
          role="menu"
          aria-label="Select language"
          className="absolute right-0 mt-1 bg-white border border-gray-200 rounded shadow-md py-1 min-w-max"
        >
          {options.map(({ code, label, flag }, index) => (
            <button
              key={code}
              role="menuitem"
              tabIndex={0}
              onClick={() => select(code)}
              onKeyDown={(e) => handleItemKeyDown(e, code, index)}
              title={label}
              aria-label={label}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-100 focus:outline-none transition-colors"
            >
              {flag}
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
